import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, Calendar, CalendarClock, Package, Trophy, Users } from "lucide-react";

import { sportsService } from "../services/sportsService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { AchievementsPanel } from "../components/sports/AchievementsPanel";
import { CompetitionsPanel } from "../components/sports/CompetitionsPanel";
import { EquipmentPanel } from "../components/sports/EquipmentPanel";
import { FixturesPanel } from "../components/sports/FixturesPanel";
import { TeamsPanel } from "../components/sports/TeamsPanel";
import { TrainingPanel } from "../components/sports/TrainingPanel";

type Tab = "fixtures" | "competitions" | "teams" | "training" | "equipment" | "achievements";

/** School sport: fixtures and results, competitions, teams and squads, training, equipment and awards. */
export default function Sports() {
  const { hasCapability, hasRole } = usePermissions();
  const canManage = hasCapability("access.sports.manager");
  const isFamily = hasRole(["parent", "student"]);
  const tabs: { id: Tab; label: string; icon: typeof Trophy }[] = [
    { id: "fixtures", label: "Fixtures", icon: CalendarClock },
    { id: "competitions", label: "Competitions", icon: Trophy },
    { id: "teams", label: "Teams", icon: Users },
    { id: "training", label: "Training", icon: Calendar },
    ...(canManage ? [{ id: "equipment" as const, label: "Equipment", icon: Package }] : []),
    { id: "achievements", label: "Achievements", icon: Award },
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "fixtures";
  const select = (next: Tab) => setParams(next === "fixtures" ? {} : { tab: next }, { replace: true });

  const queryClient = useQueryClient();
  const sports = useQuery({ queryKey: ["sports", "catalogue"], queryFn: sportsService.getSports });
  const teams = useQuery({ queryKey: ["sports", "teams"], queryFn: sportsService.getTeams });
  const competitions = useQuery({ queryKey: ["sports", "competitions"], queryFn: sportsService.getCompetitions });
  const matches = useQuery({ queryKey: ["sports", "matches"], queryFn: sportsService.getMatches });
  const training = useQuery({ queryKey: ["sports", "training"], queryFn: sportsService.getTraining });
  const achievements = useQuery({ queryKey: ["sports", "achievements"], queryFn: sportsService.getAchievements });
  const equipment = useQuery({ queryKey: ["sports", "equipment"], queryFn: sportsService.getEquipment, enabled: canManage });
  const learners = useQuery({ queryKey: ["sports", "students"], queryFn: sportsService.getStudents, enabled: canManage, staleTime: 5 * 60_000 });
  const stats = useQuery({ queryKey: ["sports", "stats"], queryFn: sportsService.getStats });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["sports"] });

  const today = todayIso();
  const fixtures = matches.data ?? [];
  const upcoming = fixtures.filter((row) => (row.status === "scheduled" || row.status === "postponed") && row.match_date >= today);
  const missing = fixtures.filter((row) => (row.status === "scheduled" || row.status === "postponed") && row.match_date < today).length;
  const live = fixtures.filter((row) => row.status === "in_progress").length;
  const next = [...upcoming].sort((a, b) => `${a.match_date}${a.match_time}`.localeCompare(`${b.match_date}${b.match_time}`))[0];
  const trainingSoon = (training.data ?? []).filter((row) => row.status === "scheduled" && row.session_date >= today).length;
  const damaged = (equipment.data ?? []).reduce((sum, row) => sum + row.damaged_quantity, 0);
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof Trophy; alert?: boolean }[] = [
    { label: "Next match", value: dash(matches, next ? `${next.home_team_name} v ${next.away_team_name}` : "None booked"), note: next ? `${next.match_date === today ? "Today" : next.match_date} at ${next.match_time.slice(0, 5)}` : `${live} playing now`, tab: "fixtures", icon: CalendarClock },
    ...(canManage
      ? [{ label: "Results to enter", value: dash(matches, String(missing + live)), note: live ? `${live} playing now` : missing ? "Past matches with no result" : "All up to date", tab: "fixtures" as Tab, icon: Trophy, alert: missing > 0 }]
      : [{ label: "Competitions under way", value: dash(stats, String(stats.data?.competitions ?? 0)), note: `${stats.data?.upcoming_matches ?? 0} matches coming up`, tab: "competitions" as Tab, icon: Trophy }]),
    { label: isFamily ? "In teams" : "Active teams", value: dash(stats, String(isFamily ? stats.data?.active_members ?? 0 : stats.data?.active_teams ?? 0)), note: isFamily ? "Places your child holds" : `${stats.data?.active_members ?? 0} players`, tab: "teams", icon: Users },
    canManage
      ? { label: "Training coming up", value: dash(training, String(trainingSoon)), note: damaged ? `${damaged} items of kit damaged` : "Kit is in order", tab: damaged ? "equipment" : "training", icon: Calendar, alert: damaged > 0 }
      : { label: "Achievements", value: dash(stats, String(stats.data?.achievements ?? 0)), note: isFamily ? "Your child's awards" : "Recorded awards", tab: "achievements", icon: Award },
  ];
  const badges: Partial<Record<Tab, number>> = { fixtures: canManage ? missing + live : live };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sports</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "Fixtures and results, competitions, teams and squads, training registers, kit and awards."
            : isFamily
              ? "When the teams play and train, how they got on, and your child's teams and awards."
              : "Fixtures, results, teams and training."}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Sports sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "fixtures" && (
        <FixturesPanel
          matches={matches.data}
          competitions={competitions.data ?? []}
          teams={teams.data ?? []}
          isLoading={matches.isLoading}
          isError={matches.isError}
          onRetry={() => matches.refetch()}
          canManage={canManage}
          onChanged={refresh}
        />
      )}
      {tab === "competitions" && (
        <CompetitionsPanel
          competitions={competitions.data}
          matches={fixtures}
          sports={sports.data ?? []}
          isLoading={competitions.isLoading}
          isError={competitions.isError}
          onRetry={() => competitions.refetch()}
          canManage={canManage}
          onChanged={refresh}
        />
      )}
      {tab === "teams" && (
        <TeamsPanel
          teams={teams.data}
          sports={sports.data ?? []}
          learners={learners.data ?? []}
          isLoading={teams.isLoading}
          isError={teams.isError}
          onRetry={() => teams.refetch()}
          canManage={canManage}
          isFamily={isFamily}
          onChanged={refresh}
        />
      )}
      {tab === "training" && (
        <TrainingPanel sessions={training.data} teams={teams.data ?? []} isLoading={training.isLoading} isError={training.isError} onRetry={() => training.refetch()} canManage={canManage} onChanged={refresh} />
      )}
      {tab === "equipment" && canManage && (
        <EquipmentPanel equipment={equipment.data} sports={sports.data ?? []} isLoading={equipment.isLoading} isError={equipment.isError} onRetry={() => equipment.refetch()} onChanged={refresh} />
      )}
      {tab === "achievements" && (
        <AchievementsPanel
          achievements={achievements.data}
          learners={learners.data ?? []}
          teams={teams.data ?? []}
          competitions={competitions.data ?? []}
          isLoading={achievements.isLoading}
          isError={achievements.isError}
          onRetry={() => achievements.refetch()}
          canManage={canManage}
          isFamily={isFamily}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
